<template>
  <div>
    <div v-if="error" class="ayuda-error">Error en el script de esta ayuda: {{ error }}</div>
    <div class="ayuda-render" ref="cont"></div>
  </div>
</template>

<script>
import { prefijarCss, animacionesDe } from "../js/AyudaUtils.js";
import RoutesUtils from "../js/RoutesUtils.js";

/**
 * Pinta el contenido de una ayuda: HTML, su CSS con el ámbito acotado a este contenedor, y su JS.
 *
 * El script recibe `root` (el contenedor) y `app` ($f7), y puede devolver una función de limpieza
 * que se invoca al cambiar de ayuda o al destruir el componente.
 */
export default {
  name: "rAyudaContenido",
  props: {
    ayuda: {
      type: Object,
      default: null
    }
  },
  data: function () {
    return {
      error: null
    };
  },
  watch: {
    ayuda: function () {
      this.pintar();
    }
  },
  mounted: function () {
    this.pintar();
  },
  beforeDestroy: function () {
    this.limpiar();
  },
  methods: {
    pintar: function () {
      var self = this;
      self.limpiar();
      var ayuda = self.ayuda;
      var cont = self.$refs.cont;
      if (!ayuda || !cont) return;

      cont.innerHTML = ayuda.html || "";

      // Un <link> dentro del contenido traería una hoja entera cuyos selectores no pasan por el
      // acotado, igual que un @import. Dentro de una ayuda no tiene ningún uso legítimo: fuera.
      self.quitar(cont.querySelectorAll("link"));

      // Los <style> que traiga el HTML tampoco pasan por el campo css: al entrar por innerHTML se
      // aplicarían tal cual a toda la aplicación. Se acotan y se sacan del contenedor por lo mismo
      // que la hoja de abajo: dentro son un hijo más y descuadran los selectores estructurales del
      // propio contenido (un `<p>…</p><style>p:last-child{…}</style>` no casaría nunca).
      var estilosDelHtml = Array.prototype.slice.call(cont.querySelectorAll("style"));

      // El CSS de una ayuda puede venir repartido entre el campo css y esos <style>, y cada hoja se
      // acota por separado. Los nombres de animación se recogen de todas antes de acotar ninguna:
      // si no, una animación declarada en una hoja y usada en otra se renombraría sólo donde se
      // declara y la referencia acabaría apuntando a la animación de la aplicación.
      var animaciones = animacionesDe(
        estilosDelHtml
          .map(function (el) { return el.textContent; })
          .concat(ayuda.css || "")
          .join("\n")
      );

      self.adoptarEstilos(estilosDelHtml, animaciones);

      if (ayuda.css) {
        var estilo = document.createElement("style");
        estilo.textContent = prefijarCss(ayuda.css, ".ayuda-render", animaciones);
        self.$el.appendChild(estilo);
        self._estilos.push(estilo);
      }

      if (ayuda.js) {
        try {
          // new Function con argumentos explícitos: el script no ve el scope del componente.
          var fn = new Function("root", "app", ayuda.js);
          var limpieza = fn(cont, self.$f7);
          if (typeof limpieza === "function") self._limpieza = limpieza;
        } catch (e) {
          // El HTML y el CSS ya están puestos: un script roto no debe dejar la ayuda en blanco.
          console.error('[ayuda] Error ejecutando el script de la ayuda "' + (ayuda.clave || "") + '"', e);
          if (self.esAdmin()) self.error = e.message;
        }
      }
    },
    quitar: function (nodos) {
      Array.prototype.slice.call(nodos).forEach(function (el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    },
    /** Acota los <style> del contenido y los mueve a la raíz, apuntándolos para la limpieza. */
    adoptarEstilos: function (nodos, animaciones) {
      var self = this;
      Array.prototype.slice.call(nodos).forEach(function (el) {
        el.textContent = prefijarCss(el.textContent, ".ayuda-render", animaciones);
        self.$el.appendChild(el);
        self._estilos.push(el);
      });
    },
    limpiar: function () {
      var self = this;
      if (typeof self._limpieza === "function") {
        try {
          self._limpieza();
        } catch (e) {
          console.error("[ayuda] Error en la limpieza del script de la ayuda", e);
        }
      }
      self._limpieza = null;
      self.error = null;
      // Las hojas ya no cuelgan del contenedor, así que vaciarlo no se las lleva.
      self.quitar(self._estilos || []);
      self._estilos = [];
      if (self.$refs.cont) self.$refs.cont.innerHTML = "";
    },
    esAdmin: function () {
      return RoutesUtils.checkRolesPermission(["ADMIN", "DTYCAL"]);
    }
  }
};
</script>

<style scoped>
.ayuda-error {
  margin: 8px 16px 0;
  padding: 8px 12px;
  border: 1px solid #f5c2c7;
  background: #f8d7da;
  color: #842029;
  border-radius: 8px;
  font-size: 12px;
}
</style>
